<template>
    <div
        :class="{
            'comment-textarea': true,
            'comment-textarea-maxLength-error': overMaxLength,
            'comment-textarea--show-limit-in-textarea': showLimitInTextarea,
        }"
    >
        <div class="comment-textarea-container">
            <textarea
                ref="commentTextarea"
                v-model.trim="commentCopy.comment_text"
                :placeholder="placeholder"
                @keydown.enter="handleEnter"
            />
            <div class="count-text">
                {{ t('snapBusiness.comment.commentLimit', {
                    count: commentTextLength, limit:
                        commentLimit
                }) }}
            </div>
        </div>
        <div class="comment-item__bottom">
            <div>
                <WorkspaceDropdown
                    v-if="!isReplyMode"
                    :options="supportedScopes"
                    :selectedOption="currentScope"
                    :sourceIn="sourceIn"
                    @change="changeCurrentScope"
                />
            </div>
            <div>
                <button
                    class="th-btn th-btn-secondary"
                    @click="cancel"
                >
                    <span>{{ t('snapBusiness.global.btn.cancel') }}</span>
                </button>
                <button
                    class="th-btn"
                    :disabled="commentTextLength === 0 || overMaxLength"
                    @click="handleSubmit(false)"
                >
                    <span>{{ t('snapBusiness.global.btn.submit') }}</span>
                </button>
            </div>
        </div>
        <ElDialog
            class="comment-textarea__change-scope-dialog"
            width="400px"
            :title="t(changeScopeDialogTitle)"
            :visible="showChangeScopeDialog"
            :append-to-body="true"
            :close-on-click-modal="false"
            :close-on-press-escape="false"
            @close="showChangeScopeDialog = false"
        >
            <div class="comment-textarea__change-scope-dialog-content">
                {{ t(changeScopeDialogContent) }}
            </div>
            <template #footer>
                <button
                    class="th-btn th-btn-secondary"
                    @click="showChangeScopeDialog = false"
                >
                    <span>{{ t('snapBusiness.global.btn.cancel') }}</span>
                </button>
                <button
                    class="th-btn"
                    @click="handleSubmit(true)"
                >
                    <span>{{ t('snapBusiness.comment.changeConfirm') }}</span>
                </button>
            </template>
        </ElDialog>
    </div>
</template>


<script setup lang="ts">
import _ from 'lodash';
import getUserDisplayInfo from 'library-@/utils/getUserDisplayInfo';
import getUserPostInfo from 'library-@/utils/getUserPostInfo';
import WorkspaceDropdown from './WorkspaceDropdown.vue';
//@ts-ignore
import { Dialog } from 'element-ui-2';
import { useI18n } from '../../composables/use-i18n';
import { ref, computed, watch } from 'vue';
import { useCommentScope } from './composables/use-comment-scope';

const ElDialog = Dialog;

/**
 * 评论数据结构
 */
type Comment = {
    comment_text?: string;
    comment_to_info?: Record<string, unknown>;
    comment_by?: string;
    comment_id?: string;
    comment_to_id?: string;
    wording_id?: string;
    source?: string;
    visible_scope?: string;
};

const props = withDefaults(defineProps<{
    comment: Comment;
    commentLength: number;
    currentUser: Record<string, unknown>;
    maxLength: number;
    showLimitInTextarea: boolean;
    sourceFrom: 'WORKSPACE' | 'ANALYTICS' | 'EUREKA' | 'VOTE';
    isPersonalAccount: boolean;
    sourceIn: 'WORKSPACE' | 'ANALYTICS' | 'EUREKA' | 'VOTE';
}>(), {
    comment: () => ({}),
    commentLength: 0,
    currentUser: () => ({}),
    maxLength: 500,
    showLimitInTextarea: false,
    sourceFrom: 'WORKSPACE',
    isPersonalAccount: false,
    sourceIn: 'WORKSPACE',
});

const { t } = useI18n();

const {
    supportedScopes,
    currentScope,
    changeCurrentScope,
    handleScopesWhenCommentChange,
    handleScopeWhenCommentChange,
} = useCommentScope();

const changeScopeDialogTitle = ref<string>('');

const changeScopeDialogContent = ref<string>('');

const showChangeScopeDialog = ref<boolean>(false);

const commentCopy = ref<Comment>({});

const commentLimit = computed(() => props.maxLength);

const commentTextLength = computed(() => {
    return _.get(commentCopy.value, 'comment_text.length') || 0;
});

const overMaxLength = computed(() => {
    return commentTextLength.value > commentLimit.value;
});

const isReplyMode = computed(() => {
    return !_.isEmpty(commentCopy.value.comment_to_info)
});

const placeholder = computed(() => {
    let placeholder = t('snapBusiness.comment.addComment');
    if (isReplyMode.value) {
        placeholder = `${t('snapBusiness.global.btn.reply')} ${getUserDisplayInfo(commentCopy.value.comment_to_info)}:`;
    }
    return placeholder;
});

function created() {
    if (props.commentLength === 0) {
        commentCopy.value.comment_by = getUserDisplayInfo(props.currentUser);
    }
}

created();

const emit = defineEmits<{
    (event: 'submit', data: { type: string, data: Comment }): void;
    (event: 'cancel'): void;
}>();

function handleSubmit(force: boolean = false) {
    const commentText = _.trim(_.get(commentCopy.value, 'comment_text'));
    if (commentText.length === 0 || commentText.length > commentLimit.value) return;
    if (commentCopy.value.comment_id) {
        const data = {
            comment_text: commentCopy.value.comment_text,
            comment_id: commentCopy.value.comment_id,
            comment_to: getUserPostInfo(commentCopy.value.comment_to_info),
            wording_id: commentCopy.value.wording_id,
            source: commentCopy.value.source,
            visible_scope: currentScope.value.key,
        };
        if (!force && commentCopy.value?.visible_scope !== currentScope.value.key && currentScope.value.key !== 'SPACE') {
            changeScopeDialogTitle.value = currentScope.value.key === 'ORG'
                ? 'snapBusiness.comment.changeCommentORG.title'
                : 'snapBusiness.comment.changeCommentPRV.title';
            changeScopeDialogContent.value = currentScope.value.key === 'ORG'
                ? 'snapBusiness.comment.changeCommentORG.content'
                : 'snapBusiness.comment.changeCommentPRV.content';
            showChangeScopeDialog.value = true;
            return;
        }
        emit('submit', { type: 'edit', data });
    } else {
        const data = {
            comment_text: commentCopy.value.comment_text,
            comment_to: getUserPostInfo(commentCopy.value.comment_to_info),
            comment_to_id: commentCopy.value.comment_to_id,
            wording_id: commentCopy.value.wording_id,
            source: commentCopy.value.source,
            visible_scope: isReplyMode.value
                ? commentCopy.value.visible_scope
                : currentScope.value.key,
        };
        emit('submit', { type: 'add', data });
    }
}

function handleEnter(e: KeyboardEvent) {
    // 输入法候选状态：Enter 仅用于选词，不触发提交
    // e.isComposing 为现代浏览器标准；keyCode === 229 兼容部分输入法回退
    if (e.isComposing || e.keyCode === 229) return;

    // Shift+Enter 走默认行为，textarea 自动换行
    if (e.shiftKey) return;

    // 纯 Enter：阻止默认换行，触发提交（复用 handleSubmit 内部空内容/超长校验）
    e.preventDefault();
    handleSubmit();
}

const commentTextarea = ref<HTMLTextAreaElement | null>(null);

function focusTextarea() {
    commentTextarea.value?.focus();
}

defineExpose({
    focusTextarea,
});

function cancel() {
    emit('cancel');
}

function handleCommentChange(comment: { visible_scope?: string }) {
    handleScopesWhenCommentChange({
        sourceFrom: props.sourceFrom,
        isPersonal: props.isPersonalAccount,
    });
    handleScopeWhenCommentChange(comment);
    commentCopy.value = _.cloneDeep(comment);
}

watch(() => props.comment, handleCommentChange, { immediate: true, deep: true });
</script>

<style lang="scss">
.comment-textarea {
    .comment-item__header-relation {
        margin-bottom: 4px;
    }

    .comment-textarea-container {
        width: 100%;
        overflow: hidden;
        position: relative;

        textarea {
            width: 100%;
            height: calc(100% - 4px);
            padding: 8px;
            color: #061632;
            border-radius: 4px;
            resize: none;
            outline: none;

            &:focus {
                border-color: $brand-primary;
            }

            &::placeholder {
                color: #b3bac5;
            }

            &.focus-visible {
                border-color: $brand-primary !important;
            }
        }
    }

    .comment-item__header {
        &-relation {
            width: 100%;
        }
    }

    .comment-item__bottom {
        margin-top: 4px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        position: relative;

        .pt-ui-btn {
            margin-right: 8px;
            font-size: 14px;

            &:last-of-type {
                margin-right: 0;
            }
        }
    }

    .count-text {
        font-size: 12px;
        position: absolute;
        bottom: 10px;
        right: 20px;
        color: #b3bac5;
    }
    &__change-scope-dialog {
        .el-dialog__title {
            font-size: 16px;
            color: $text-primary;
        }
        .el-dialog__body {
            color: $text-primary;
        }
        &-content {
            word-break: break-word;
        }
    }
}

.comment-textarea-maxLength-error {
    .comment-textarea-container {
        textarea {
            border: 1px solid #d11a32 !important;
        }
    }

    .count-text {
        color: #d11a32;
    }
}

.comment-textarea--show-limit-in-textarea {
    .comment-item__bottom {
        .count-text {
            display: none;
        }
    }

    &.comment-textarea-maxLength-error {
        .comment-textarea-container {
            border: 1px solid #d11a32 !important;
        }
    }

    .comment-textarea-container {
        border: 1px solid #d2d2d2;
        border-radius: 4px;

        .comment-textarea-limit {
            display: block;
        }

        textarea {
            border: none !important;
            border-radius: 0;
        }
    }
}
</style>
